import { useCometAvailableDxts } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometAvailableDxts.js';
import { ConnectionStatus } from '#pplx/frontend/apps/ask/connectors/consts/connectionStatus.js';
import { CONNECTOR_DETAILS } from '#pplx/frontend/apps/ask/connectors/consts/connectorDetails.js';
import { PROVIDER_FOOTNOTES, buildConnectorDetailsFromSource, hasSourceDetailsMetadata, toSampleQueries, } from '#pplx/frontend/apps/ask/connectors/utils/connectorDetails.js';
import { formatConnectorName } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
export const useConnectorDetails = (connectorName, connectionStatus = ConnectionStatus.DISCONNECTED, source, provider, description) => {
    const { $t } = useIntl();
    const isEnterprise = useIsEnterpriseUser();
    const availableDxts = useCometAvailableDxts();
    const mcpb = useMemo(() => availableDxts?.find((dxt) => dxt.id === connectorName), [availableDxts, connectorName]);
    const sourceProvider = provider ?? (source && 'provider' in source ? source.provider : undefined);
    return useMemo(() => {
        const sourceDetails = source?.source_details;
        // Sample queries only ever come from the DDB record, but they must not decide
        // which base wins (see hasSourceDetailsMetadata), so they are merged onto
        // whichever one does.
        const sampleQueries = toSampleQueries(sourceDetails?.sample_queries);
        if (sourceDetails && hasSourceDetailsMetadata(sourceDetails, sourceProvider)) {
            return buildConnectorDetailsFromSource({
                sourceDetails,
                connectorName,
                connectionStatus,
                isEnterprise,
                displayName: source?.display_name,
                description: source?.description,
                provider: sourceProvider,
            });
        }
        const staticDetails = CONNECTOR_DETAILS[connectorName];
        if (staticDetails) {
            const { name, websiteUrl, documentationUrl, tagline, supportUrl = 'https://www.perplexity.ai/help-center/en/articles/10672063-introduction-to-file-connectors-for-enterprise-organizations', footnote, consumerBullets, bullets, connectedBullets, developer, } = staticDetails;
            const links = [{ label: 'Website', url: websiteUrl, icon: $icon('link') }];
            if (documentationUrl) {
                links.push({
                    label: 'Documentation',
                    url: documentationUrl,
                    icon: $icon('book'),
                });
            }
            if (supportUrl) {
                links.push({ label: 'Support', url: supportUrl, icon: $icon('help') });
            }
            let finalBullets = bullets;
            if (connectionStatus === ConnectionStatus.CONNECTED && connectedBullets) {
                finalBullets = connectedBullets;
            }
            else if (!isEnterprise && consumerBullets) {
                finalBullets = consumerBullets;
            }
            return {
                name: source?.display_name || name || formatConnectorName(connectorName),
                tagline: tagline ? $t(tagline) : null,
                footnote: footnote ? { link: footnote.link, text: $t(footnote.text) } : null,
                bullets: finalBullets.map((bullet) => $t(bullet)),
                links,
                developer: $t(developer),
                sampleQueries,
            };
        }
        if (mcpb) {
            const { name, display_name, description, long_description, homepage } = mcpb.manifest;
            const links = [];
            if (homepage) {
                links.push({ label: 'Website', url: homepage, icon: $icon('link') });
            }
            return {
                name: display_name ?? name,
                tagline: description,
                footnote: null,
                bullets: long_description
                    ?.split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean) ?? [],
                links,
                developer: null,
                sampleQueries,
            };
        }
        return {
            name: source?.display_name || formatConnectorName(connectorName),
            tagline: description ?? null,
            footnote: sourceProvider ? (PROVIDER_FOOTNOTES[sourceProvider] ?? null) : null,
            bullets: [],
            links: [],
            developer: null,
            sampleQueries,
        };
    }, [
        source?.source_details,
        source?.description,
        source?.display_name,
        sourceProvider,
        connectorName,
        description,
        mcpb,
        connectionStatus,
        isEnterprise,
        $t,
    ]);
};
//# sourceMappingURL=useConnectorDetails.js.map